// Κοινά κομμάτια των παραθύρων προκαταβολής/επιστροφής: πλαίσιο, γραμμή παραστατικού, μηνύματα
// αποτελέσματος. Ύφος ταμείου (surface/line/t1… tokens, φωτεινό και σκούρο).

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertOctagon, Loader2, X } from 'lucide-react';
import { Btn, Note } from '../dineIn/floorEditor/ui';
import type { ChargeOutcome } from './api';
import { docNumber } from './model';
import type { FiscalRef } from './types';

/** Κείμενα του namespace deposits (πάντα με πλήρες κλειδί «deposits:…»). */
export function useDT() {
  const { t } = useTranslation(['deposits']);
  return (key: string, vars?: Record<string, unknown>) => String(t(key, vars as never));
}

/**
 * Πλαίσιο παραθύρου πάνω από όλα (Κρατήσεις z-50/60, «Ταμείο» z-70). Δεν κλείνει όσο χρεώνει.
 * `wide`: φαρδύτερο, για παράθυρο με αριθμητικό δίπλα στη λίστα (προκαταβολή στο Take Away).
 */
export function Shell({ title, sub, closable, onClose, footer, children, testId, wide }: {
  title: string; sub?: React.ReactNode; closable: boolean; onClose: () => void;
  footer?: React.ReactNode; children: React.ReactNode; testId?: string; wide?: boolean;
}) {
  const dt = useDT();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (closable) onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [closable, onClose]);
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-black/50 p-0 sm:p-4" onClick={() => { if (closable) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label={title} data-testid={testId}
        className={`flex max-h-full w-full ${wide ? 'max-w-[880px]' : 'max-w-[560px]'} flex-col overflow-hidden bg-surface-1 shadow-xl sm:max-h-[92vh] sm:rounded-2xl sm:border sm:border-line`}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-start gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0 flex-1">
            <h3 className="text-[19px] font-bold text-t1">{title}</h3>
            {sub && <p className="mt-0.5 truncate text-[13px] text-t3">{sub}</p>}
          </div>
          <Btn size="icon" aria-label={dt('deposits:close')} disabled={!closable} onClick={onClose}><X className="h-4 w-4" /></Btn>
        </div>
        <div className="grid min-h-0 flex-1 auto-rows-max content-start gap-4 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

/** «Περιμένουμε το τερματικό» — μεγάλο, για να το βλέπει ο ταμίας από απόσταση. */
export function Waiting({ title, hint }: { title: string; hint?: string }) {
  return (
    <div role="status" aria-live="polite" data-testid="deposit-charging"
      className="grid justify-items-center gap-2 rounded-xl border border-blue-500/30 bg-blue-500/8 px-4 py-6 text-center">
      <Loader2 className="h-9 w-9 animate-spin text-blue-600 dark:text-blue-400" />
      <p className="text-[17px] font-bold text-t1">{title}</p>
      {hint && <p className="text-[13px] text-t3">{hint}</p>}
    </div>
  );
}

/** Ισχυρή προειδοποίηση «δεν ξέρουμε αν χρεώθηκε». */
export function Uncertain({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div role="alert" data-testid="deposit-uncertain" className="grid gap-1.5 rounded-xl border-2 border-red-500/60 bg-red-500/10 px-4 py-3">
      <p className="flex items-start gap-2 text-[15px] font-bold text-red-700 dark:text-red-300">
        <AlertOctagon className="mt-0.5 h-5 w-5 shrink-0" />{title}
      </p>
      {children && <div className="pl-7 text-[13px] text-t2">{children}</div>}
    </div>
  );
}

/**
 * Το παραστατικό μιας προκαταβολής (8.4) ή επιστροφής (8.5): σειρά/αριθμός/MARK, ή «σε εκκρεμότητα»
 * (ο server το ξαναστέλνει μόνος του). Μετρητά: κανένα παραστατικό.
 */
export function ReceiptLine({ doc, receipt, pending, error, cash, compact }: {
  doc: '8.4' | '8.5'; receipt: FiscalRef | null | undefined; pending?: boolean; error?: string | null; cash?: boolean; compact?: boolean;
}) {
  const dt = useDT();
  if (cash) return <span className="text-[12.5px] text-t3">{dt('deposits:receipt.none')}</span>;
  const marked = !!(receipt && receipt.mark);
  if (receipt && marked && !pending) {
    return (
      <span className="text-[12.5px] text-t2" data-testid="deposit-receipt">
        <span className="font-semibold text-t1">{doc} {docNumber(receipt)}</span>
        {!compact && <span className="text-t3"> · {dt('deposits:receipt.mark', { mark: receipt.mark })}</span>}
      </span>
    );
  }
  return (
    <span className="text-[12.5px] font-semibold text-amber-700 dark:text-amber-300" data-testid="deposit-receipt-pending"
      title={error || dt('deposits:receipt.pendingHint')}>
      {receipt && docNumber(receipt) ? `${doc} ${docNumber(receipt)} · ` : ''}{dt('deposits:receipt.pending', { doc })}
      {!compact && error && <span className="block font-normal text-t3">{error}</span>}
    </span>
  );
}

/** Το μήνυμα ενός αποτελέσματος για τον ταμία: πρώτα του server (ελληνικά), αλλιώς από τον κωδικό. */
export function outcomeText<T>(dt: ReturnType<typeof useDT>, out: ChargeOutcome<T> | null): string {
  if (!out || out.kind === 'ok') return '';
  if (out.kind === 'uncertain' || out.kind === 'declined') return out.message || '';
  if (out.message) return out.message;
  if (out.code) {
    const byCode = dt(`deposits:code.${out.code}`, { defaultValue: '' });
    if (byCode) return byCode;
  }
  return out.kind === 'failed' ? '' : dt('deposits:err.generic');
}

/** Απόρριψη / σφάλμα πάνω από τη φόρμα. */
export function OutcomeNote<T>({ out, declinedTitle }: { out: ChargeOutcome<T> | null; declinedTitle: string }) {
  const dt = useDT();
  if (!out || out.kind === 'ok' || out.kind === 'uncertain') return null;
  const text = outcomeText(dt, out);
  if (out.kind === 'declined') {
    return (
      <div data-testid="deposit-declined">
        <Note tone="error"><b>{declinedTitle}</b>{text ? `: ${text}` : ''}<span className="block">{dt('deposits:modal.declinedNext')}</span></Note>
      </div>
    );
  }
  if (out.kind === 'failed') {
    return (
      <div data-testid="deposit-failed">
        <Note tone="error"><b>{dt('deposits:modal.failed')}</b>{text ? `: ${text}` : ''}<span className="block">{dt('deposits:modal.declinedNext')}</span></Note>
      </div>
    );
  }
  return <div data-testid="deposit-error"><Note tone="warn">{text}</Note></div>;
}

/** Κουμπιά επιλογής (τρόπος, τερματικό) — μεγάλοι στόχοι αφής. */
export function Choice({ on, disabled, onClick, children, testId }: {
  on: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode; testId?: string;
}) {
  return (
    <button type="button" aria-pressed={on} disabled={disabled} onClick={onClick} data-testid={testId}
      className={`min-h-[48px] rounded-xl border-2 px-3 text-[15px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${on
        ? 'border-blue-600 bg-blue-600/10 text-blue-700 dark:border-blue-400 dark:text-blue-300'
        : 'border-line bg-surface-1 text-t1 hover:border-line-strong'}`}>
      {children}
    </button>
  );
}
